import React, { useState } from 'react';

import { useIntl } from 'react-intl';

import { IconButton, type IconButtonProps } from '@atlaskit/button/new';
import { cssMap, cx } from '@atlaskit/css';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { TeamInlinePreview } from '@atlassian/team-inline-preview';

import type { AtlassianTeamUpdateArchiveHydratedAction } from '../../../types';
import { formatTeamMembersPreviewCount } from '../../../utils/team-members-preview';
import messages from '../../messages';

const styles = cssMap({
	teamRow: {
		width: '100%',
		textAlign: 'start',
		paddingBlock: token('space.075'),
		paddingInline: token('space.100'),
		borderRadius: token('radius.large'),
		backgroundColor: token('elevation.surface'),
		cursor: 'pointer',
		'&:hover': {
			backgroundColor: token('elevation.surface.hovered'),
		},
		'&:active': {
			backgroundColor: token('elevation.surface.pressed'),
		},
	},
	hiddenActionsBase: {
		display: 'none',
	},
	hiddenActionsVisible: {
		display: 'flex',
	},
	teamRowDisabled: {
		pointerEvents: 'none',
	},
	teamRowContentDisabled: {
		opacity: token('opacity.disabled'),
	},
	iconWrapper: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		paddingBlock: token('space.050'),
		paddingInline: token('space.050'),
	},
});

type TeamItemActionProps = Omit<IconButtonProps, 'onClick' | 'label'> & {
	onClick: (team: AtlassianTeamUpdateArchiveHydratedAction) => Promise<void> | void;
	label: string;
};

type TeamItemProps = {
	team: AtlassianTeamUpdateArchiveHydratedAction;
	onClick?: () => void;
	actions?: TeamItemActionProps[];
	hoverActions?: TeamItemActionProps[];
	isArchiving?: boolean;
	hasErrored?: boolean;
};

const iconButtonDefaultProps: Omit<IconButtonProps, 'icon' | 'label'> = {
	isTooltipDisabled: false,
	appearance: 'subtle',
	spacing: 'compact',
} as const;

const IconWrapper = ({ children }: { children: React.ReactNode }) => {
	return <Box xcss={styles.iconWrapper}>{children}</Box>;
};

export const TeamItem = ({
	team,
	onClick,
	actions,
	hoverActions,
	isArchiving = false,
	hasErrored = false,
}: TeamItemProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [isHovered, setIsHovered] = useState(false);
	const [isActionLoading, setIsActionLoading] = useState(false);
	const members = team.data.members?.edges?.map((e) => e?.node?.member).filter(Boolean) ?? [];
	const memberCount = formatTeamMembersPreviewCount(members.length);
	const teamTypeName = team.data.type?.name;
	const isDisabled = isArchiving || isActionLoading;

	const getDescription = () => {
		const hasMembersData = team.data.members?.edges != null;

		if (teamTypeName && hasMembersData) {
			return formatMessage(messages.teamDescription, {
				teamType: teamTypeName,
				memberCount,
			});
		}
		if (teamTypeName) {
			return teamTypeName;
		}
		if (hasMembersData) {
			return formatMessage(messages.teamDescriptionMembersOnly, {
				memberCount,
			});
		}
		return undefined;
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
		if (onClick && (e.key === 'Enter' || e.key === ' ')) {
			e.preventDefault();
			onClick();
		}
	};

	const renderActionButton = (props: TeamItemActionProps, index: number) => {
		return (
			<IconButton
				key={`${props.label}-${index}`}
				{...iconButtonDefaultProps}
				{...props}
				onClick={async (e) => {
					e.stopPropagation();
					setIsActionLoading(true);
					await props.onClick?.(team);
					setIsActionLoading(false);
				}}
			/>
		);
	};

	const renderActions = () => {
		if (hasErrored) {
			return (
				<Tooltip content={formatMessage(messages.errorTooltip)}>
					<IconWrapper>
						<StatusErrorIcon label="" color={token('color.icon.warning')} size="medium" />
					</IconWrapper>
				</Tooltip>
			);
		}
		if (isArchiving) {
			return (
				<IconWrapper>
					<Spinner size="small" />
				</IconWrapper>
			);
		}
		if (isActionLoading) {
			return null;
		}
		return (
			<>
				{hoverActions && hoverActions.length > 0 && (
					<Inline xcss={cx(styles.hiddenActionsBase, isHovered && styles.hiddenActionsVisible)}>
						{hoverActions.map(renderActionButton)}
					</Inline>
				)}
				{actions?.map(renderActionButton)}
			</>
		);
	};

	return (
		<Box
			testId="archive-teams-card-team-item"
			onClick={onClick}
			xcss={cx(styles.teamRow, isDisabled && styles.teamRowDisabled)}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
			onFocus={() => setIsHovered(true)}
			onBlur={(e: React.FocusEvent<HTMLDivElement>) => {
				if (!e.currentTarget.contains(e.relatedTarget)) {
					setIsHovered(false);
				}
			}}
			tabIndex={onClick ? 0 : undefined}
			onKeyDown={handleKeyDown}
		>
			<Inline space="space.100" alignBlock="center" spread="space-between">
				<Box xcss={isDisabled ? styles.teamRowContentDisabled : undefined}>
					<TeamInlinePreview
						teamId={team.data.teamId}
						teamName={team.data.displayName ?? ''}
						overrideAvatarUrl={team.data.largeAvatarImageUrl ?? undefined}
						description={getDescription()}
						isVerified={team.data.isVerified ?? undefined}
						singleLine
					/>
				</Box>
				<Inline space="space.050" alignBlock="center">
					{renderActions()}
				</Inline>
			</Inline>
		</Box>
	);
};
